import { defineStore } from 'pinia';
import { useNuxtApp, useRuntimeConfig, createError } from '#imports';
import { useCmsProxy } from '~dsc/composables/useCmsProxy';

export const usePageStore = defineStore('page', {
    state: () => ({
        pageData: [],
        pageBuffer: [],
        pageTitle: '',
        loading: false,
        slug: '',
        localizedPagePaths: {},
        station: {},
        stationBuffer: {},
        meta: {},
    }),
    actions: {
        // absolutely no use server-side
        present() {
            this.pageData = this.pageBuffer;
            this.station = this.stationBuffer;
        },
        async pageLoad(route = {}) {
            const { $paths } = useNuxtApp();
            const stationPages = useRuntimeConfig()?.public?.locator?.stationPages || {};
            const lunchListEnabled = stationPages.lunchList ?? stationPages.enabled ?? false;
            const foodMenuEnabled = stationPages.foodMenu ?? stationPages.enabled ?? false;
            let assignedContent;

            try {
                this.loading = true;

                const res = await useCmsProxy($paths.getEndpoint('page'), {
                    query: {
                        path: route.path,
                    },
                }, route);

                if (!res) return false;

                const { localizedPagePaths, content } = res;

                if (content?.station && typeof content.station === 'object' && !Array.isArray(content.station)) {
                    const station = Object.assign(
                        content.station,
                        { image: content.image },
                        { ...(lunchListEnabled ? { lunch: content.lunchList } : {}) },
                        { ...(foodMenuEnabled ? { menu: content.foodMenu } : {}) },
                    );
                    import.meta.server ? this.station = station : this.stationBuffer = station;
                    delete content.station;
                    const body = Array.isArray(content.body) ? content.body : [];
                    assignedContent = [
                        {
                            contentType: 'stations',
                            type: 'header',
                        },
                        lunchListEnabled && {
                            contentType: 'stations',
                            type: 'lunch',
                        },
                        ...body,
                        {
                            contentType: 'stations',
                            type: 'attributes',
                        },
                        foodMenuEnabled && {
                            contentType: 'stations',
                            type: 'foodMenu',
                        },
                        {
                            contentType: 'stations',
                            type: 'map',
                        },
                    ].filter(Boolean);
                } else if (content?.contentType === 'articlePage') {
                    const { id, title, byline, image, alt, created, articleContent, tags } = content;
                    const article = {
                        contentType: 'articleBlock',
                        id,
                        title,
                        byline,
                        image,
                        alt,
                        created,
                        articleContent,
                        tags,
                    };

                    if (Array.isArray(content.body)) {
                        content?.body?.unshift(article);
                    } else {
                        content.body = [article];
                    }

                    assignedContent = content?.body;
                } else {
                    assignedContent = content?.body ?? [];
                    import.meta.server ? this.station = {} : this.stationBuffer = {};
                }

                this.slug = content?.slug === '/' ? 'landing' : content?.slug ?? route.path.replace(/^\/|\/$/g, '').replace(/\//g, '-');
                this.pageTitle = content?.title;
                this.localizedPagePaths = localizedPagePaths;
                this.loading = false;

                this.meta = {
                    noIndex: content?.noIndex ?? null,
                    description: content?.description ?? null,
                    ogTitle: content?.openGraphTitle ?? null,
                    ogDescription: content?.openGraphDescription ?? null,
                    ogImage: content?.openGraphImage ?? null,
                };

                if (import.meta.server) {
                    this.pageData = assignedContent;
                } else {
                    this.pageBuffer = assignedContent;
                }

                return true;
            } catch (e) {
                throw createError(e);
            }
        },
    },
    getters: {
        getTitle: state => state.pageTitle,
        getPageData: state => state.pageData,
        getLoading: state => state.loading,
        getLocalizedPagePaths: state => state.localizedPagePaths,
        getStation: state => state.station,
    },
});
